import {
  ChangeDetectionStrategy,
  Component,
  input,
  signal,
} from '@angular/core';

import { Payment } from '../../../models/payment.model';

export interface TransactionGroup {
  month: string;
  payments: Payment[];
}

@Component({
  selector: 'app-transaction-list',
  templateUrl: './transaction-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class TransactionListComponent {
  readonly payments = input<Payment[]>([]);
  readonly isPaymentsLoading = input<boolean>(false);
  readonly isExporting = signal(false);

  get groupedPayments(): TransactionGroup[] {
    const monthMap = new Map<string, { month: string; payments: Payment[] }>();

    for (const p of this.payments()) {
      const d = new Date(p.Transaction_Date__c + 'T00:00:00');
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const month = d.toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric',
      });

      if (!monthMap.has(monthKey)) {
        monthMap.set(monthKey, { month, payments: [] });
      }
      monthMap.get(monthKey)!.payments.push(p);
    }

    return Array.from(monthMap.values());
  }

  getStatusLabel(payment: Payment): string {
    switch (payment.Billing_Status__c) {
      case 'Billed':
        return 'Applied';
      case 'Unbilled':
        return 'Pending';
      default:
        return payment.Billing_Status__c ?? '—';
    }
  }

  getStatusClasses(payment: Payment): Record<string, boolean> {
    switch (payment.Billing_Status__c) {
      case 'Billed':
        return { 'bg-green-100': true, 'text-green-700': true };
      case 'Unbilled':
        return { 'bg-gray-100': true, 'text-gray-700': true };
      default:
        return { 'bg-gray-100': true, 'text-gray-600': true };
    }
  }

  getDisplayAmount(payment: Payment): number {
    return payment.Transaction_Amount__c != null
      ? Math.abs(payment.Transaction_Amount__c)
      : 0;
  }

  canExport(): boolean {
    return this.payments()?.length > 0 || false;
  }

  async downloadCsv(): Promise<void> {
    this.isExporting.set(true);
    try {
      const headers = ['Date', 'Type', 'Amount', 'Status'];
      const rows = this.payments()?.map((p) => [
        p.Transaction_Date__c,
        'Payment',
        p.Transaction_Amount__c != null
          ? `$${this.getDisplayAmount(p).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : '',
        this.getStatusLabel(p),
      ]);

      const csv = [headers, ...rows]
        .map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','),
        )
        .join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'transactions.csv';
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      this.isExporting.set(false);
    }
  }
}
